import { Switchable } from '@codex-brightworld/gadgets';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled from 'styled-components';

import DefaultTab from './Tab';

export const TabsHeader = styled.ul`
  display: flex;
  margin: 0;
  padding: 0;
  list-style: none;
  position: relative;
  bottom: -1px;
`;

const Tab = styled(DefaultTab)``;

export const TabsWrapperHeader = styled.div``;

export const TabsContent = styled.div`
  padding: 15px;
  font-family: ${({ theme }) => theme.fonts.default.fontFamily};
`;

export const TabItem = ({ label, children = undefined, ...customProps }) => null;

/**
 * @description
 * The Tabs component accepts its items either as a JSON prop `tabItems` or JSX `children`
 * - JSON array prop `tabItems={[ {label, content}, ... ]}`
 * - JSX children `<TabItem label={label}>{content}</TabItem>...`
 *
 * @see [`./index.spec.js`](./index.spec.js) for example usage
 *
 * note:
 * - Both approaches support custom props.
 * - It is possible to mix both tabItems and children, but please don't.
 */
export default class Tabs extends PureComponent {
  render() {
    const { props } = this;
    const tabItems = [].concat(
      props.tabItems,
      React.Children.map(props.children, kid => ({ content: kid.props.children, ...kid.props }))
    );

    return (
      <Switchable current={props.defaultActive} data-qa={props.qa}>
        {({ current, switchTo }) => (
          <>
            <TabsWrapperHeader>
              <TabsHeader>
                {tabItems.map((tab, index) => (
                  <Tab
                    /*
                      We pass all props here, because Tab is just a function call
                      and dev might need to pass some custom props.
                    */
                    {...props}
                    {...tab}
                    active={index === current}
                    key={index}
                    index={index}
                    siblingsCount={tabItems.length}
                    onClick={() => {
                      switchTo(index);
                      props.onChange(index);
                    }}
                  >
                    {tab.label}
                  </Tab>
                ))}
              </TabsHeader>
            </TabsWrapperHeader>
            {props.renderContent(
              TabsContent,
              tabItems[current] ? tabItems[current].content : undefined,
              current,
              { ...props, ...tabItems[current] }
            )}
          </>
        )}
      </Switchable>
    );
  }
}

Tabs.displayName = 'Tabs';

Tabs.propTypes = {
  defaultActive: PropTypes.number,
  onChange: PropTypes.func,
  tabItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node,
      content: PropTypes.node
      // you can also pass other custom props
    })
  ),
  children: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.element
  ]),
  qa: PropTypes.string,
  renderContent: PropTypes.func,
  renderTab: PropTypes.func
};

Tabs.defaultProps = {
  defaultActive: 0,
  onChange: () => { },
  qa: undefined,
  renderContent: (DefaultContentWrapper, content, currentTabIndex, props) => (
    <DefaultContentWrapper>
      {content}
    </DefaultContentWrapper>
  ),
  renderTab: undefined,
  tabItems: [],
  children: []
};
